import { addDays, addHours, addMonths, addWeeks, endOfDay, endOfMonth, endOfYear, set } from "date-fns";

export type DateTimeExtensionType = "Default" | "Relative";

export type DateTimeExtensionUnit =
	| "Hours"
	| "Days"
	| "Weeks"
	| "Months"
	| "EndOfDay"
	| "EndOfNextDay"
	| "EndOfWeek"
	| "EndOfMonth"
	| "EndOfYear";

export interface DateTimeExtensionPolicy {
	type?: DateTimeExtensionType;
	unit?: DateTimeExtensionUnit;
	amount?: number;
	fixedTime?: { hours: number; minutes: number };
}

export interface DateTimeSettings {
	useTwentyFourHourClock: boolean;
	dateFormat: number;
	firstDayOfWeek: number;
}

const isDelayAmountRequiredForDateTimeExtensionUnit = (unit: DateTimeExtensionUnit): boolean => {
	return ["Hours", "Days", "Weeks", "Months"].includes(unit);
};

/**
 * Last instant of the calendar week that contains {@code dateTime}, where the week starts on {@code firstDayOfWeek}
 * (0 = Sunday … 6 = Saturday), matching org {@code firstDayOfWeek} / server deadline policy.
 */
const endOfWeekForWeekStartingOn = (dateTime: Date, firstDayOfWeek: number): Date => {
	const day = dateTime.getDay();
	const daysFromWeekStart = (day - firstDayOfWeek + 7) % 7;
	const weekStart = addDays(dateTime, -daysFromWeekStart);
	return endOfDay(addDays(weekStart, 6));
};

export const getDateTimeForDateTimeExtensionPolicy = (
	dateTime: string | Date,
	policy: DateTimeExtensionPolicy | undefined,
	organizationDateTimeSettings: DateTimeSettings,
): Date => {
	let extendedDateTime = new Date(dateTime);

	if (!policy || policy.type === "Default") {
		return addWeeks(extendedDateTime, 1);
	}

	const isDelayRequiredForPolicyUnit = isDelayAmountRequiredForDateTimeExtensionUnit(policy.unit);
	if (isDelayRequiredForPolicyUnit) {
		switch (policy.unit) {
			case "Hours":
				extendedDateTime = addHours(extendedDateTime, policy.amount);
				break;
			case "Days":
				extendedDateTime = addDays(extendedDateTime, policy.amount);
				break;
			case "Weeks":
				extendedDateTime = addWeeks(extendedDateTime, policy.amount);
				break;
			case "Months":
				extendedDateTime = addMonths(extendedDateTime, policy.amount);
				break;
		}
	} else if (policy.unit === "EndOfWeek") {
		const rawOrganizationFirstDayOfWeek = organizationDateTimeSettings.firstDayOfWeek;
		const organizationFirstDayOfWeek = Math.min(6, Math.max(0, rawOrganizationFirstDayOfWeek ?? 1));
		extendedDateTime = endOfWeekForWeekStartingOn(extendedDateTime, organizationFirstDayOfWeek);
	} else {
		switch (policy.unit) {
			case "EndOfDay":
				extendedDateTime = endOfDay(extendedDateTime);
				break;
			case "EndOfNextDay":
				extendedDateTime = endOfDay(addDays(extendedDateTime, 1));
				break;
			case "EndOfMonth":
				extendedDateTime = endOfMonth(extendedDateTime);
				break;
			case "EndOfYear":
				extendedDateTime = endOfYear(extendedDateTime);
				break;
		}
	}

	if (policy.fixedTime != null) {
		extendedDateTime = set(extendedDateTime, {
			hours: policy.fixedTime.hours,
			minutes: policy.fixedTime.minutes,
			seconds: 0,
			milliseconds: 0,
		});
	}

	return extendedDateTime;
};
